import { jsPDF } from 'jspdf'
import { parsePlanBlocks, parseInlineSegments } from './planTextParser'

const MARGIN = 48

// Las fuentes estándar de jsPDF no incluyen símbolos como "→" o
// dígitos en subíndice (usados a veces por texto generado por IA,
// p. ej. "VO₂max"); se sustituyen por su equivalente en texto plano
// para que no salgan huecos o caracteres rotos en el PDF.
function sanitizeForPdf(text) {
  const subDigits = '₀₁₂₃₄₅₆₇₈₉'
  return (text || '')
    .replace(/→/g, '->')
    .replace(/←/g, '<-')
    .replace(/[₀₁₂₃₄₅₆₇₈₉]/g, c => String(subDigits.indexOf(c)))
    .replace(/[\u{1F300}-\u{1FAFF}]|[☀-➿]/gu, '')
}

function createCursor(doc) {
  const pageW = doc.internal.pageSize.getWidth()
  const pageH = doc.internal.pageSize.getHeight()
  return {
    doc, x: MARGIN, y: MARGIN, pageW, pageH,
    maxWidth: pageW - MARGIN * 2,
    ensure(height) {
      if (this.y + height > this.pageH - MARGIN) {
        this.doc.addPage()
        this.y = MARGIN
      }
    },
  }
}

function drawWrapped(cursor, segments, { fontSize, bold = false, spacingAfter = 10 }) {
  const { doc } = cursor
  const lineHeight = fontSize * 1.4
  doc.setFontSize(fontSize)
  cursor.ensure(lineHeight)

  const words = []
  segments.forEach(seg => {
    seg.text.split(/(\s+)/).forEach(w => { if (w) words.push({ text: w, bold: seg.bold || bold }) })
  })

  let x = cursor.x
  words.forEach(w => {
    doc.setFont('helvetica', w.bold ? 'bold' : 'normal')
    const isSpace = /^\s+$/.test(w.text)
    const width = doc.getTextWidth(w.text)
    if (!isSpace && x + width > cursor.x + cursor.maxWidth) {
      x = cursor.x
      cursor.y += lineHeight
      cursor.ensure(lineHeight)
    }
    if (!(isSpace && x === cursor.x)) {
      doc.text(w.text, x, cursor.y)
      x += width
    }
  })
  cursor.y += lineHeight + spacingAfter
}

function drawListItem(cursor, text, fontSize) {
  const bulletIndent = 14
  cursor.doc.setFontSize(fontSize)
  cursor.doc.setFont('helvetica', 'normal')
  cursor.ensure(fontSize * 1.4)
  cursor.doc.text('•', cursor.x, cursor.y)

  const savedX = cursor.x
  const savedMaxWidth = cursor.maxWidth
  cursor.x += bulletIndent
  cursor.maxWidth -= bulletIndent
  drawWrapped(cursor, parseInlineSegments(text), { fontSize, spacingAfter: 4 })
  cursor.x = savedX
  cursor.maxWidth = savedMaxWidth
}

export function buildPlanPdf({ patientNombre, contenido }) {
  const doc = new jsPDF({ unit: 'pt', format: 'a4' })
  const cursor = createCursor(doc)
  const clean = sanitizeForPdf(contenido)

  doc.setFontSize(9)
  doc.setTextColor('#6b7280')
  doc.setFont('helvetica', 'normal')
  doc.text('PLAN NUTRICIONAL', cursor.x, cursor.y)
  cursor.y += 20

  doc.setFontSize(20)
  doc.setTextColor('#111111')
  doc.setFont('helvetica', 'bold')
  doc.text(patientNombre || 'Paciente', cursor.x, cursor.y)

  const today = new Date().toLocaleDateString('es', { day: 'numeric', month: 'long', year: 'numeric' })
  doc.setFontSize(9)
  doc.setFont('helvetica', 'normal')
  doc.setTextColor('#6b7280')
  const genText = `Generado: ${today}`
  doc.text(genText, cursor.x + cursor.maxWidth - doc.getTextWidth(genText), cursor.y - 6)

  cursor.y += 14
  doc.setDrawColor('#111111')
  doc.setLineWidth(1.2)
  doc.line(cursor.x, cursor.y, cursor.x + cursor.maxWidth, cursor.y)
  cursor.y += 24

  const blocks = parsePlanBlocks(clean)
  blocks.forEach((block, idx) => {
    doc.setTextColor('#111111')

    if (block.type === 'space') { cursor.y += 6; return }

    if (block.type === 'h2') {
      cursor.ensure(40)
      if (idx > 0) {
        doc.setDrawColor('#e5e7eb')
        doc.setLineWidth(0.75)
        doc.line(cursor.x, cursor.y, cursor.x + cursor.maxWidth, cursor.y)
        cursor.y += 16
      }
      drawWrapped(cursor, parseInlineSegments(block.text), { fontSize: 14, bold: true, spacingAfter: 6 })
      return
    }

    if (block.type === 'h3') {
      drawWrapped(cursor, parseInlineSegments(block.text), { fontSize: 11.5, bold: true, spacingAfter: 4 })
      return
    }

    if (block.type === 'list') {
      block.items.forEach(item => drawListItem(cursor, item, 11))
      cursor.y += 4
      return
    }

    drawWrapped(cursor, parseInlineSegments(block.text), { fontSize: 11 })
  })

  const pageCount = doc.getNumberOfPages()
  for (let p = 1; p <= pageCount; p++) {
    doc.setPage(p)
    doc.setFontSize(8)
    doc.setTextColor('#9ca3af')
    doc.setFont('helvetica', 'normal')
    doc.text('NutriControl · Documento confidencial · Uso clínico exclusivo', MARGIN, cursor.pageH - 24)
  }

  return doc
}

export function downloadPlanPdf({ patientNombre, contenido }) {
  const doc = buildPlanPdf({ patientNombre, contenido })
  const fecha = new Date().toLocaleDateString('es').replace(/\//g, '-')
  const nombre = (patientNombre || 'plan').replace(/\s+/g, '_')
  doc.save(`Plan_${nombre}_${fecha}.pdf`)
}
